import { ThemeProvider } from '@servicetim/ui/components';
import { ApiError, type AppConfig, ConfigContext, SessionProvider } from '@servicetim/ui/lib';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { useState } from 'react';
import type { AppRouter } from '@/routes/router';

export interface AppProps {
  config: AppConfig;
  router: AppRouter;
}

/** Query defaults: 4xx answers are final (no retry); network/5xx retry once. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) =>
          failureCount < 1 &&
          !(error instanceof ApiError && error.status >= 400 && error.status < 500),
        refetchOnWindowFocus: false,
      },
      mutations: { retry: false },
    },
  });
}

/** Provider stack: runtime config → TanStack Query → session (OIDC, dev claims or disabled) → router. */
export function App({ config, router }: AppProps) {
  const [queryClient] = useState(createQueryClient);

  return (
    <ConfigContext.Provider value={config}>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <ThemeProvider>
            <RouterProvider router={router} />
          </ThemeProvider>
        </SessionProvider>
      </QueryClientProvider>
    </ConfigContext.Provider>
  );
}
